import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpenCheck, ExternalLink, FileText, Link2, ShieldCheck } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { FactsVsNarrative } from "@/components/FactsVsNarrative";
import { FullArticle } from "@/components/FullArticle";
import { renderWithRedactions } from "@/components/TextFormatter";
import { SocialShareCard } from "@/components/SocialShareCard";
import { RelatedDossiers } from "@/components/RelatedDossiers";
import { toPng } from "html-to-image";
import { useRef } from "react";
import { TOPIC } from "@/constants/testIds";
import { displayTitleForTopic } from "@/lib/factAlignment";
import { sourceDisplayOutlet } from "@/lib/sourceLabels";

const CATEGORY_LABEL = {
        politica: "Política",
        media: "Media",
        justica: "Justiça",
        economico: "Económico",
        social: "Social",
        historico: "Histórico",
        ciencia_saude: "Ciência e Saúde",
        religiao: "Religião",
        esoterismo: "Esoterismo",
};

const collectCitationNumbers = (...texts) => {
        const refs = new Set();
        texts.forEach((text) => {
                String(text || "").replace(/\[([1-9]\d*)\]/g, (_, n) => {
                        refs.add(Number(n));
                        return "";
                });
        });
        return [...refs].sort((a, b) => a - b);
};

const factText = (fact) => {
        if (!fact) return "";
        if (typeof fact === "string") return fact;
        return fact.truth || fact.media_claim || fact.description || fact.date || "";
};

const factSourceIndex = (fact, sources = []) => {
        if (!fact || typeof fact !== "object") return null;
        const raw = Number(fact.source_idx || fact.sourceIndex || fact.source);
        return Number.isInteger(raw) && raw >= 1 && raw <= sources.length ? raw : null;
};

const EvidenceBasePanel = ({ topic, compact = false }) => {
        const sources = topic.sources || [];
        const facts = topic.verified_facts || [];
        const citationNumbers = collectCitationNumbers(
                topic.summary,
                topic.full_article,
                topic.deception_strategy,
                topic.ai_analysis
        );
        const validCitationNumbers = citationNumbers.filter((n) => n >= 1 && n <= sources.length);
        const invalidCitationNumbers = citationNumbers.filter((n) => n < 1 || n > sources.length);
        const factSourceRefs = facts
                .map((fact) => factSourceIndex(fact, sources))
                .filter(Boolean);
        const uniqueFactSourceRefs = [...new Set(factSourceRefs)];
        const limits = [];

        if (sources.length === 0) {
                limits.push("Este dossiê não tem fontes anexadas.");
        }
        if (sources.length > 0 && citationNumbers.length === 0) {
                limits.push("O texto não apresenta citações inline para ligar afirmações a anexos específicos.");
        }
        if (invalidCitationNumbers.length > 0) {
                limits.push(`Há citações sem fonte correspondente: ${invalidCitationNumbers.map((n) => `[${n}]`).join(", ")}.`);
        }
        if (facts.length > 0 && uniqueFactSourceRefs.length === 0 && sources.length > 0) {
                limits.push("Os factos documentados existem, mas nem todos apontam diretamente para um anexo numerado.");
        }

        return (
                <section className={`border border-[var(--line-strong)] bg-[var(--paper)] relative ${compact ? "p-5" : "p-6 md:p-8"}`}>
                        <div className="absolute -top-3 left-6 stamp-truth stamp">
                                Base factual
                        </div>
                        <div className={`mt-3 grid gap-0 border border-[var(--line)] bg-[var(--bg-deep)] ${compact ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4"}`}>
                                {[
                                        ["Fontes", sources.length, "var(--ai)"],
                                        ["Factos", facts.length, "var(--truth)"],
                                        ["Citações", validCitationNumbers.length, "var(--controversy)"],
                                        ["Índice", `${topic.controversy_score ?? 0}/100`, "var(--ink)"],
                                ].map(([label, value, color]) => (
                                        <div
                                                key={label}
                                                className={`${compact ? "p-3" : "p-4"} border-r border-b border-[var(--line)] last:border-r-0`}
                                        >
                                                <div className="overline text-[var(--ink-faint)] mb-2">
                                                        {label}
                                                </div>
                                                <div className={`font-serif leading-none ${compact ? "text-2xl" : "text-3xl"}`} style={{ color }}>
                                                        {value}
                                                </div>
                                        </div>
                                ))}
                        </div>

                        <div className="mt-8">
                                <div>
                                        <div className="overline text-[var(--truth)] mb-4 flex items-center gap-2">
                                                <ShieldCheck size={14} strokeWidth={1.5} />
                                                Factos que sustentam a leitura
                                        </div>
                                        <div className="space-y-4">
                                                {facts.slice(0, compact ? 3 : 4).map((fact, index) => {
                                                        const sourceIndex = factSourceIndex(fact, sources);
                                                        const text = factText(fact);
                                                        return (
                                                                <div key={`${text}-${index}`} className="border-b border-[var(--line)] last:border-b-0 pb-4 last:pb-0">
                                                                        <div className="flex items-start gap-3">
                                                                                <span className="line-no mt-1">
                                                                                        F.{String(index + 1).padStart(2, "0")}
                                                                                </span>
                                                                                <div>
                                                                                        <p className="font-serif text-base leading-relaxed text-[var(--ink)]">
                                                                                                {renderWithRedactions(text, sources)}
                                                                                        </p>
                                                                                        {sourceIndex && (
                                                                                                <a
                                                                                                        href={`#src-${sourceIndex}`}
                                                                                                        className="mt-2 inline-flex items-center gap-2 font-mono text-[11px] text-[var(--controversy)] hover:text-[var(--ink)]"
                                                                                                >
                                                                                                        <Link2 size={12} strokeWidth={1.5} />
                                                                                                        Anexo [{sourceIndex}]
                                                                                                </a>
                                                                                        )}
                                                                                </div>
                                                                        </div>
                                                                </div>
                                                        );
                                                })}
                                                {facts.length === 0 && (
                                                        <p className="text-sm text-[var(--ink-faint)] font-mono">
                                                                Sem factos documentados catalogados.
                                                        </p>
                                                )}
                                        </div>
                                </div>
                        </div>

                        <div className="mt-8 border-t border-[var(--line-strong)] pt-5">
                                <div className="overline text-[var(--ink-faint)] mb-2 flex items-center gap-2">
                                        <BookOpenCheck size={14} strokeWidth={1.5} />
                                        Limites de leitura
                                </div>
                                {limits.length > 0 ? (
                                        <ul className="space-y-2">
                                                {limits.map((limit) => (
                                                        <li key={limit} className="text-sm text-[var(--ink-soft)] font-mono">
                                                                {limit}
                                                        </li>
                                                ))}
                                        </ul>
                                ) : (
                                        <p className="text-sm text-[var(--ink-soft)] font-mono">
                                                O dossiê liga a análise a fontes anexadas e a citações numeradas no texto.
                                        </p>
                                )}
                        </div>
                </section>
        );
};

export default function TopicDetail() {
        const { slug } = useParams();
        const [topic, setTopic] = useState(null);
        const [error, setError] = useState(null);
        const shareRef = useRef(null);

        const handleDownloadShareCard = async () => {
                if (!shareRef.current) return;
                try {
                        const dataUrl = await toPng(shareRef.current, { 
                                cacheBust: true,
                                width: 1080,
                                height: 1080,
                                pixelRatio: 1
                        });
                        const link = document.createElement("a");
                        link.download = `dossie-${slug}.png`;
                        link.href = dataUrl;
                        link.click();
                } catch (err) {
                        console.error("Failed to generate image", err);
                }
        };

        useEffect(() => {
                window.scrollTo(0, 0);
                setTopic(null);
                setError(null);
                api.get(`/topics/${slug}`)
                        .then((r) => setTopic(r.data))
                        .catch((e) =>
                                setError(
                                        e.response?.status === 404
                                                ? "Dossiê não encontrado."
                                                : "Erro ao carregar dossiê."
                                )
                        );
        }, [slug]);

        if (error) {
                return (
                        <main className="mx-auto max-w-[1100px] px-6 lg:px-12 py-32 text-center">
                                <p className="font-serif text-5xl text-[var(--ink)]">{error}</p>
                                <Link
                                        to="/"
                                        className="overline mt-8 inline-block link-underline text-[var(--controversy)]"
                                        data-testid={TOPIC.backLink}
                                >
                                        ← Voltar ao arquivo
                                </Link>
                        </main>
                );
        }

        if (!topic) {
                return (
                        <main className="mx-auto max-w-[1100px] px-6 lg:px-12 py-32 text-center font-mono text-[var(--ink-soft)]">
                                A descodificar dossiê...
                        </main>
                );
        }

        const score = topic.controversy_score ?? 0;
        const displayTitle = displayTitleForTopic(topic);
        const created = topic.created_at
                ? new Date(topic.created_at).toLocaleDateString("pt-PT", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                  })
                : "";

        return (
                <main>
                        {/* Hidden Social Share Card */}
                        <SocialShareCard ref={shareRef} topic={topic} />

                        {/* Hero */}
                        <section className="border-b border-[var(--line-strong)] relative overflow-hidden">
                                <div className="absolute inset-0 crosshair-bg opacity-20 pointer-events-none" />
                                
                                <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-12 relative flex flex-col md:flex-row gap-12 justify-between items-start">
                                        
                                        <div className="relative z-10 max-w-4xl">
                                                <Link
                                                        to="/"
                                                        data-testid={TOPIC.backLink}
                                                        className="overline text-[var(--ink-soft)] inline-flex items-center gap-2 hover:text-[var(--controversy)] transition-colors"
                                                >
                                                        <ArrowLeft size={14} strokeWidth={1.5} /> Voltar ao
                                                        arquivo
                                                </Link>

                                                <div className="mt-8 flex flex-wrap items-center gap-3">
                                                        <span
                                                                data-testid={TOPIC.detailCategory}
                                                                className="overline px-3 py-1 border border-[var(--controversy)] text-[var(--controversy)]"
                                                        >
                                                                {CATEGORY_LABEL[topic.category]}
                                                        </span>
                                                        <span className="overline text-[var(--ink-faint)]">
                                                                Catalogado · {created}
                                                        </span>
                                                        <span className="stamp">
                                                                Dossiê #{(topic.id || "").slice(0, 8)}
                                                        </span>
                                                        <span className="stamp-gold stamp">
                                                                Índice {score}/100
                                                        </span>
                                                </div>

                                                <h1
                                                        data-testid={TOPIC.detailTitle}
                                                        className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mt-8 text-[var(--ink)]"
                                                >
                                                        {renderWithRedactions(displayTitle)}
                                                </h1>
                                                <p className="font-serif italic text-xl md:text-2xl leading-snug text-[var(--ink-soft)] mt-8 max-w-3xl">
                                                        {renderWithRedactions(topic.summary)}
                                                </p>

                                                <button
                                                        onClick={handleDownloadShareCard}
                                                        className="mt-8 flex items-center gap-2 bg-[var(--ink)] text-[var(--paper)] px-5 py-3 font-mono text-sm tracking-widest uppercase hover:bg-[var(--controversy)] transition-colors"
                                                >
                                                        Descarregar Prova (Partilhar)
                                                </button>
                                        </div>

                                        {/* Polaroid Style Attached Photo */}
                                        {topic.image_url && (
                                                <div className="relative z-0 shrink-0 w-full md:w-72 lg:w-96 rotate-2 md:rotate-3 lg:rotate-6 p-3 bg-white shadow-xl border border-gray-200 hidden md:block">
                                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-12 h-6 bg-[rgba(255,255,255,0.7)] border border-gray-200/50 shadow-sm backdrop-blur-sm -rotate-2" /> {/* tape effect */}
                                                        <div className="w-full aspect-[4/3] bg-gray-100 overflow-hidden relative">
                                                                <img 
                                                                        src={topic.image_url.startsWith('/uploads/') ? `${BACKEND_URL}${topic.image_url}` : topic.image_url}
                                                                        alt="Anexo" 
                                                                        className="w-full h-full object-cover filter grayscale contrast-125 sepia-[.3]"
                                                                />
                                                        </div>
                                                        <div className="mt-3 flex items-center justify-between opacity-60 px-1">
                                                                <span className="font-mono text-[9px] uppercase tracking-widest text-black">
                                                                        Anexo {(topic.id || "0000").slice(0, 4)}
                                                                </span>
                                                                <span className="font-mono text-[9px] text-black">
                                                                        CONFIDENCIAL
                                                                </span>
                                                        </div>
                                                </div>
                                        )}

                                </div>
                        </section>

                        {/* Body */}
                        <section className="mx-auto max-w-[1400px] px-6 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
                                <div className="lg:col-span-8 space-y-20">
                                        {/* Full article — primary investigative piece */}
                                        {topic.full_article && (
                                                <div data-testid="topic-detail-full-article">
                                                        <div className="overline text-[var(--ink-faint)] mb-4 flex items-center gap-3">
                                                                <FileText size={11} strokeWidth={1.5} />
                                                                Investigação · peça completa
                                                                {topic.auto_generated && (
                                                                        <span className="stamp-ai stamp text-[9px] py-0.5 px-2">
                                                                                Gerada por pesquisa pública
                                                                        </span>
                                                                )}
                                                        </div>
                                                        <FullArticle
                                                                article={topic.full_article}
                                                                sources={topic.sources}
                                                        />
                                                </div>
                                        )}

                                        {/* Facts vs Narrative */}
                                        <div>
                                                <div className="overline text-[var(--ink-faint)] mb-4">
                                                        Secção 02 · Contraste documental
                                                </div>
                                                <FactsVsNarrative
                                                        facts={topic.verified_facts}
                                                        narratives={topic.media_narratives}
                                                        topic={topic}
                                                />
                                        </div>

                                        {/* Deception strategy */}
                                        {topic.deception_strategy && (
                                                <div
                                                        data-testid={TOPIC.deceptionBlock}
                                                        className="relative border border-[var(--line-strong)] bg-[var(--paper)] p-8 md:p-12"
                                                >
                                                        <div className="absolute -top-4 left-8 stamp">
                                                                Estratégia identificada
                                                        </div>
                                                        <div className="overline text-[var(--deception)] mt-2 mb-3">
                                                                Secção 03 · Como se constrói a percepção
                                                        </div>
                                                        <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] mb-6 text-[var(--ink)]">
                                                                A engenharia da{" "}
                                                                <span
                                                                        className="italic"
                                                                        style={{ color: "var(--deception)" }}
                                                                >
                                                                        ilusão.
                                                                </span>
                                                        </h2>
                                                        <p className="text-lg leading-relaxed text-[var(--ink)] font-serif">
                                                                {renderWithRedactions(topic.deception_strategy, topic.sources)}
                                                        </p>
                                                </div>
                                        )}

                                        {/* AI Analysis */}
                                        {topic.ai_analysis && (
                                                <div data-testid={TOPIC.aiAnalysisBlock}>
                                                        <div className="flex items-center justify-between mb-4">
                                                                <div
                                                                        className="overline"
                                                                        style={{ color: "var(--ai)" }}
                                                                >
                                                                        Secção 04 · Análise cruzada
                                                                </div>
                                                                {topic.ai_model_used && (
                                                                        <span className="stamp-ai stamp">
                                                                                Análise de Especialistas
                                                                        </span>
                                                                )}
                                                        </div>
                                                        <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] mb-8 text-[var(--ink)]">
                                                                O que{" "}
                                                                <span
                                                                        className="italic"
                                                                        style={{ color: "var(--ai)" }}
                                                                >
                                                                        escapa
                                                                </span>{" "}
                                                                ao debate público.
                                                        </h2>
                                                        <div className="text-lg leading-[1.75] space-y-6 text-[var(--ink)] font-serif border-l border-[var(--line-strong)] pl-6 md:pl-8">
                                                                        {topic.ai_analysis
                                                                                .split(/\n\n+/)
                                                                                .map((paragraph, idx) => (
                                                                                        <p key={idx}>{renderWithRedactions(paragraph, topic.sources)}</p>
                                                                                ))}
                                                        </div>
                                                </div>
                                        )}

                                </div>

                                {/* Sidebar */}
                                <aside className="lg:col-span-4 lg:border-l border-[var(--line-strong)] lg:pl-8 space-y-12">
                                        <EvidenceBasePanel topic={topic} compact />

                                        <div data-testid={TOPIC.sourcesList}>
                                                <div className="overline text-[var(--ink-faint)] mb-3 flex items-center gap-2">
                                                        <FileText size={11} strokeWidth={1.5} /> Anexos · fontes
                                                </div>
                                                <h3 className="font-serif text-2xl leading-tight mb-4 text-[var(--ink)]">
                                                        Para verificar.
                                                </h3>
                                                <ul className="space-y-4">
                                                        {(!topic.sources || topic.sources.length === 0) && (
                                                                <li className="text-sm text-[var(--ink-faint)] italic">
                                                                        Sem fontes documentadas.
                                                                </li>
                                                        )}
                                                        {topic.sources?.map((s, i) => (
                                                                <li key={s.url} className="hairline pb-4 flex gap-3 scroll-mt-24" id={`src-${i + 1}`}>
                                                                        <span
                                                                                className="font-mono text-xs mt-1 flex-shrink-0"
                                                                                style={{ color: "var(--controversy)" }}
                                                                        >
                                                                                [{i + 1}]
                                                                        </span>
                                                                        <a
                                                                                href={s.url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="group block flex-1"
                                                                        >
                                                                                <span className="overline text-[var(--ai)] block">
                                                                                        {sourceDisplayOutlet(s)}
                                                                                </span>
                                                                                <span className="font-serif text-lg leading-tight inline-flex items-start gap-2 mt-1 text-[var(--ink)] group-hover:text-[var(--controversy)] transition-colors">
                                                                                        {s.title}
                                                                                        <ExternalLink
                                                                                                size={12}
                                                                                                strokeWidth={1.5}
                                                                                                className="mt-[8px] flex-shrink-0 opacity-50 group-hover:opacity-100"
                                                                                        />
                                                                                </span>
                                                                        </a>
                                                                </li>
                                                        ))}
                                                </ul>
                                        </div>

                                        {topic.tags?.length > 0 && (
                                                <div>
                                                        <div className="overline text-[var(--ink-faint)] mb-3">
                                                                Etiquetas
                                                        </div>
                                                        <div className="flex flex-wrap gap-1">
                                                                {topic.tags.map((t) => (
                                                                        <span
                                                                                key={t}
                                                                                className="border border-[var(--line-strong)] px-2 py-1 text-[10px] uppercase font-mono tracking-wider text-[var(--ink-soft)]"
                                                                        >
                                                                                {t}
                                                                        </span>
                                                                ))}
                                                        </div>
                                                </div>
                                        )}

                                        <div>
                                                <div className="overline text-[var(--ink-faint)] mb-3">
                                                        Índice de controvérsia
                                                </div>
                                                <div
                                                        className="font-serif text-7xl font-bold leading-none"
                                                        style={{
                                                                color:
                                                                        score > 70
                                                                                ? "var(--deception)"
                                                                                : score > 40
                                                                                ? "var(--controversy)"
                                                                                : "var(--truth)",
                                                        }}
                                                >
                                                        {score}
                                                        <span className="text-3xl text-[var(--ink-faint)]">
                                                                /100
                                                        </span>
                                                </div>
                                                <div className="score-bar mt-4">
                                                        <span style={{ width: `${score}%` }} />
                                                </div>
                                        </div>
                                </aside>
                        </section>
                        
                        <RelatedDossiers currentSlug={topic.slug} />
                </main>
        );
}
